
import { type LexicalEditor } from 'lexical';
import { useRef } from 'react';
import { clamp } from '../../../lib/Math';
import './Resizer.css';

export type ImageResizerProperties =
{
    editor: LexicalEditor;
    targetRef: {current: null | HTMLElement};
    maxWidth?: number;
    onResizeEnd: (width: number, height: number) => void;
    onResizeStart: () => void;
    setShowCaption: (show: boolean) => void;
    showCaption: boolean;
}

const Direction =
{
    north: 1,
    south: 2,
    east: 4,
    west: 8
};

class Positioning
{
    currentHeight = 0;
    currentWidth = 0;
    direction = 0;
    isResizing = false;
    ratio = 0;
    startHeight = 0;
    startWidth = 0;
    startX = 0;
    startY = 0;
}

export default function Resizer
(
    {
        onResizeStart,
        onResizeEnd,
        targetRef,
        maxWidth,
        editor,
        showCaption,
        setShowCaption
    }
    : ImageResizerProperties
)
{
    const buttonRef = useRef(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const positioningRef = useRef<Positioning>(new Positioning());
    const activePointerIdRef = useRef<number | null>(null);
    const capturedElementRef = useRef<HTMLElement | null>(null);
    const editorRootElement = editor.getRootElement();
    const maxWidthContainer = maxWidth ? maxWidth : editorRootElement !== null ? editorRootElement.getBoundingClientRect().width - 20 : 100;
    const maxHeightContainer = editorRootElement !== null ? editorRootElement.getBoundingClientRect().height - 20 : 100;
    const minWidth = 20;
    const minHeight = 20;

    const setStartCursor = (direction: number) =>
    {
        const ew = direction === Direction.east || direction === Direction.west;
        const ns = direction === Direction.north || direction === Direction.south;
        const nwse = (direction & Direction.north && direction & Direction.west)
            || (direction & Direction.south && direction & Direction.east);
        const cursorDir = ew ? 'ew' : ns ? 'ns' : nwse ? 'nwse' : 'nesw';

    };
    const setEndCursor = () => {};
    const handlePointerMove = (event: PointerEvent) =>
    {
        const target = targetRef.current;
        const positioning = positioningRef.current;
        const isHorizontal = positioning.direction & (Direction.east | Direction.west);
        const isVertical = positioning.direction & (Direction.south | Direction.north);
        if (target === null || positioning.isResizing === false) { return; }
        if (isHorizontal && isVertical)
        {
            let diff = Math.floor(positioning.startX - event.clientX);
            diff = positioning.direction & Direction.east ? -diff : diff;
            const width = clamp(positioning.startWidth + diff, minWidth, maxWidthContainer);
            const height = width / positioning.ratio;
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
            positioning.currentHeight = height;
            positioning.currentWidth = width;
        }
        else if (isVertical)
        {
            let diff = Math.floor(positioning.startY - event.clientY);
            diff = positioning.direction & Direction.south ? -diff : diff;
            const height = clamp(positioning.startHeight + diff, minHeight, maxHeightContainer);
            target.style.height = `${height}px`;
            positioning.currentHeight = height;
        }
        else
        {
            let diff = Math.floor(positioning.startX - event.clientX);
            diff = positioning.direction & Direction.east ? -diff : diff;
            const width = clamp(positioning.startWidth + diff, minWidth, maxWidthContainer);
            target.style.width = `${width}px`;
            positioning.currentWidth = width;
        }
    };
    const handlePointerUp = () =>
    {
        const target = targetRef.current;
        const positioning = positioningRef.current;
        const wrapper = wrapperRef.current;
        if (!target || !wrapper || positioning.isResizing === false) { return; }
        const width = positioning.currentWidth;
        const height = positioning.currentHeight;
        positioning.startWidth = 0;
        positioning.startHeight = 0;
        positioning.ratio = 0;
        positioning.startX = 0;
        positioning.startY = 0;
        positioning.currentWidth = 0;
        positioning.currentHeight = 0;
        positioning.isResizing = false;
        setEndCursor();
        onResizeEnd(width, height);
        if (capturedElementRef.current && activePointerIdRef.current !== null)
        {
            try { (wrapper as unknown as HTMLElement).releasePointerCapture(activePointerIdRef.current); }
            catch { /* empty */ }
            activePointerIdRef.current = null;
        }
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('blur', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp, true);
    };
    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>, direction: number) =>
    {
        event.preventDefault();
        event.stopPropagation();
        const target = targetRef.current;
        const wrapper = wrapperRef.current;
        if (!target || !wrapper) { return; }
        const { width, height } = target.getBoundingClientRect();
        const positioning = positioningRef.current;
        positioning.startWidth = width;
        positioning.startHeight = height;
        positioning.ratio = width / height;
        positioning.currentWidth = width;
        positioning.currentHeight = height;
        positioning.startX = event.clientX;
        positioning.startY = event.clientY;
        positioning.isResizing = true;
        positioning.direction = direction;
        const currentTarget = event.currentTarget as HTMLElement;
        currentTarget.setPointerCapture(event.pointerId);
        capturedElementRef.current = currentTarget;
        activePointerIdRef.current = event.pointerId;
        setStartCursor(direction);
        onResizeStart();
        target.style.height = `${height}px`;
        target.style.width = `${width}px`;
        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('blur', handlePointerUp, { once: true }); // fallback
        document.addEventListener('pointercancel', handlePointerUp, { capture: true });
    };
    const element =
    <div ref={wrapperRef}>
        <div className="resizer resizer-n" onPointerDown={(event) => { handlePointerDown(event, Direction.north); }} />
        <div className="resizer resizer-ne" onPointerDown={(event) => { handlePointerDown(event, Direction.north | Direction.east); }} />
        <div className="resizer resizer-e" onPointerDown={(event) => { handlePointerDown(event, Direction.east); }} />
        <div className="resizer resizer-se" onPointerDown={(event) => { handlePointerDown(event, Direction.south | Direction.east); }} />
        <div className="resizer resizer-s" onPointerDown={(event) => { handlePointerDown(event, Direction.south); }} />
        <div className="resizer resizer-sw" onPointerDown={(event) => { handlePointerDown(event, Direction.south | Direction.west); }} />
        <div className="resizer resizer-w" onPointerDown={(event) => { handlePointerDown(event, Direction.west); }} />
        <div className="resizer resizer-nw" onPointerDown={(event) => { handlePointerDown(event, Direction.north | Direction.west); }} />
    </div>;
    return element;
}

